import React, { useEffect, useRef } from 'react';
import { Box, BoxProps, Typography } from '@mui/material';
import { DesignMode, getDesignTokens } from './designTokens';

type SpaceShaderPanelProps = BoxProps & {
  mode?: DesignMode;
  word?: string;
};

const vertexShaderSource = `
  attribute vec2 a_position;

  void main() {
    gl_Position = vec4(a_position, 0.0, 1.0);
  }
`;

const fragmentShaderSource = `
  #define TWO_PI 6.2831853072
  #define PI 3.14159265359

  precision highp float;
  uniform vec2 resolution;
  uniform float time;

  void main(void) {
    vec2 uv = (gl_FragCoord.xy * 2.0 - resolution.xy) / min(resolution.x, resolution.y);
    float t = time*0.05;
    float lineWidth = 0.002;

    vec3 color = vec3(0.0);
    for(int j = 0; j < 3; j++){
      for(int i=0; i < 5; i++){
        color[j] += lineWidth*float(i*i) / abs(fract(t - 0.01*float(j)+float(i)*0.01)*5.0 - length(uv) + mod(uv.x+uv.y, 0.2));
      }
    }

    gl_FragColor = vec4(color[0],color[1],color[2],1.0);
  }
`;

const createShader = (
  gl: WebGLRenderingContext,
  type: number,
  source: string
) => {
  const shader = gl.createShader(type);
  if (!shader) return null;

  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }

  return shader;
};

const createProgram = (gl: WebGLRenderingContext) => {
  const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
  const fragmentShader = createShader(
    gl,
    gl.FRAGMENT_SHADER,
    fragmentShaderSource
  );

  if (!vertexShader || !fragmentShader) return null;

  const program = gl.createProgram();
  if (!program) return null;

  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  gl.deleteShader(vertexShader);
  gl.deleteShader(fragmentShader);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    gl.deleteProgram(program);
    return null;
  }

  return program;
};

export const SpaceShaderPanel = ({
  mode = 'light',
  word = 'INCRYPT',
  sx,
  ...props
}: SpaceShaderPanelProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const tokens = getDesignTokens(mode);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const gl = canvas.getContext('webgl', {
      alpha: false,
      antialias: true,
      preserveDrawingBuffer: false
    });
    if (!gl) return undefined;

    const program = createProgram(gl);
    if (!program) return undefined;

    const positionBuffer = gl.createBuffer();
    const positionLocation = gl.getAttribLocation(program, 'a_position');
    const resolutionLocation = gl.getUniformLocation(program, 'resolution');
    const timeLocation = gl.getUniformLocation(program, 'time');
    let shaderTime = 1.0;
    let animationFrame = 0;

    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW
    );

    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      const width = Math.max(1, Math.floor(rect.width * pixelRatio));
      const height = Math.max(1, Math.floor(rect.height * pixelRatio));

      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }

      gl.viewport(0, 0, width, height);
    };

    const render = () => {
      resizeCanvas();
      gl.useProgram(program);
      gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
      gl.enableVertexAttribArray(positionLocation);
      gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);
      gl.uniform2f(resolutionLocation, canvas.width, canvas.height);
      gl.uniform1f(timeLocation, shaderTime);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      shaderTime += 0.05;
      animationFrame = window.requestAnimationFrame(render);
    };

    animationFrame = window.requestAnimationFrame(render);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      gl.deleteBuffer(positionBuffer);
      gl.deleteProgram(program);
    };
  }, []);

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: { xs: 320, md: 460 },
        overflow: 'hidden',
        isolation: 'isolate',
        borderRadius: 3,
        border: `1px solid ${
          mode === 'light' ? 'rgba(15,23,42,0.1)' : 'rgba(255,255,255,0.16)'
        }`,
        background: '#000',
        boxShadow:
          mode === 'light'
            ? 'inset 0 1.5px 0 rgba(255,255,255,0.42), inset 0 -1px 0 rgba(255,255,255,0.16), 0 18px 44px rgba(15,23,42,0.16)'
            : 'inset 0 1.5px 0 rgba(255,255,255,0.22), inset 0 -1px 0 rgba(0,0,0,0.4), 0 18px 48px rgba(0,0,0,0.32)',
        ...sx
      }}
      {...props}
    >
      <Box
        component="canvas"
        ref={canvasRef}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          display: 'block'
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          pointerEvents: 'none',
          background:
            'linear-gradient(135deg, rgba(255,255,255,0.12), transparent 28%, transparent 68%, rgba(255,255,255,0.1))',
          boxShadow: `inset 0 0 0 1px ${tokens.colors.border}`
        }}
      />
      <Typography
        component="div"
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#ffffff',
          fontSize: { xs: 48, sm: 68, md: 96 },
          fontWeight: 900,
          lineHeight: 1,
          letterSpacing: 0,
          textAlign: 'center',
          textShadow:
            '0 0 18px rgba(255,255,255,0.46), 0 0 42px rgba(65,200,196,0.5), 0 12px 34px rgba(0,0,0,0.38)',
          fontFamily: 'var(--design-font-display)'
        }}
      >
        {word}
      </Typography>
    </Box>
  );
};
